<template>
  <div class="members-table">
    <div class="members-table__wrapper">
      <table class="members-table__table">
        <thead class="members-table__header">
          <tr>
            <th class="members-table__th">Name</th>
            <th class="members-table__th">Date Requested</th>
            <th class="members-table__th">Email Address</th>
            <th class="members-table__th">Role</th>
            <th class="members-table__th members-table__th--actions">Actions</th>
          </tr>
        </thead>
        <tbody class="members-table__body">
          <tr v-for="member in members" :key="member.id" class="members-table__row">
            <td class="members-table__td">
              <div class="members-table__name">
                <div class="members-table__avatar">
                  {{ getInitials(member.fname, member.lname) }}
                </div>
                <span>{{ member.fname }} {{ member.lname }}</span>
              </div>
            </td>
            <td class="members-table__td">
              <span class="members-table__date">{{ formatDate(member.createdAt) }}</span>
            </td>
            <td class="members-table__td">
              <span class="members-table__email">{{ member.email }}</span>
            </td>
            <td class="members-table__td">
              <span class="members-table__role-badge">
                {{ member.groups[0]?.name ?? "No role" }}
              </span>
            </td>
            <td class="members-table__td members-table__td--actions">
              <Popper offsetDistance="5" placement="bottom-end">
                <button class="members-table__actions-btn">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" fill="currentColor"/>
                  </svg>
                </button>
                <template #content>
                  <div class="members-table__dropdown">
                    <button
                      class="members-table__dropdown-item members-table__dropdown-item--danger"
                      :disabled="!isActionAllowed"
                      @click="deleteMember(member.id)"
                    >
                      Remove Member
                    </button>
                  </div>
                </template>
              </Popper>
            </td>
          </tr>
          <tr v-if="members.length === 0 && !loading">
            <td colspan="5" class="members-table__empty">
              <div class="members-table__empty-content">
                <div class="members-table__empty-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <p>You currently do not have any team members</p>
              </div>
            </td>
          </tr>
          <tr v-if="loading">
            <td colspan="5" class="members-table__loading">
              <div class="members-table__spinner"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
    
<script>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Popper from "vue3-popper";

export default {
  props: {
    members: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isActionAllowed: Boolean,
  },

  emits: ["delete-member"],

  components: {
    Popper,
  },

  setup(props, { emit }) {
    const searchQuery = ref("");
    const moreActionsId = ref(null);

    function formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString();
    }

    function getInitials(fname, lname) {
      const first = fname ? fname.charAt(0).toUpperCase() : '';
      const last = lname ? lname.charAt(0).toUpperCase() : '';
      return first + last;
    }

    function handleMoreActions(role) {
      moreActionsId.value = moreActionsId.value === role.id ? null : role.id;
    }

    function deleteMember(memberId) {
      moreActionsId.value = null;
      emit("delete-member", memberId);
    }

    const handleOutsideClick = (event) => {
      if (moreActionsId.value !== null && !event.target.closest(".actions")) {
        moreActionsId.value = null;
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleOutsideClick);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleOutsideClick);
    });

    watch(
      () => props.members,
      () => {
        moreActionsId.value = null;
      }
    );

    return {
      moreActionsId,
      formatDate,
      getInitials,
      handleMoreActions,
      deleteMember,
    };
  },
};
</script>

<style lang="scss" scoped>
.members-table {
  width: 100%;
  
  &__wrapper {
    overflow-x: auto;
  }
  
  &__table {
    width: 100%;
    border-collapse: collapse;
  }
  
  &__header {
    background: #F8FAFC;
  }
  
  &__th {
    padding: 14px 20px;
    text-align: left;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #64748B;
    white-space: nowrap;
    
    &--actions {
      text-align: right;
    }
  }
  
  &__body {
    background: #ffffff;
  }
  
  &__row {
    border-bottom: 1px solid #E2E8F0;
    transition: background 0.2s ease;
    
    &:hover {
      background: #F8FAFC;
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  &__td {
    padding: 16px 20px;
    vertical-align: middle;
    
    &--actions {
      text-align: right;
    }
  }
  
  &__name {
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
  }
  
  &__avatar {
    width: 36px;
    height: 36px;
    background: #EEF4FF;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #4D84FF;
  }
  
  &__date {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
  }
  
  &__email {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
  }
  
  &__role-badge {
    display: inline-flex;
    padding: 6px 12px;
    background: #F1F5F9;
    border-radius: 20px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #64748B;
  }
  
  &__actions-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: #64748B;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-left: auto;
    
    &:hover {
      background: #F1F5F9;
      color: #0F172A;
    }
  }
  
  &__dropdown {
    min-width: 160px;
    background: #ffffff;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  }
  
  &__dropdown-item {
    width: 100%;
    padding: 10px 12px;
    text-align: left;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s ease;
    
    &:hover:not(:disabled) {
      background: #F1F5F9;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    &--danger {
      color: #EF4444;
      
      &:hover:not(:disabled) {
        background: #FEF2F2;
      }
    }
  }
  
  &__empty {
    padding: 48px 20px;
  }
  
  &__empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    
    p {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748B;
      margin: 0;
    }
  }
  
  &__empty-icon {
    width: 48px;
    height: 48px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    margin-bottom: 12px;
  }
  
  &__loading {
    padding: 48px 20px;
    text-align: center;
  }
  
  &__spinner {
    width: 24px;
    height: 24px;
    border: 2px solid #E2E8F0;
    border-top-color: #4D84FF;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>